import {ROLES} from '../constants/portalsConstants';
import {checkUserLoggedIn, convertSecondsToReadable} from '../helpers/Utils';
import handleError from './errorMiddleware';

export const FETCH_ERROR_CODE = 'FETCH_ERROR';
const PARSING_ERROR_CODE = 'PARSING_ERROR';
const EXPECTED_ERROR = 'Expected an error argument here';

/**
 * Extracts site name and ID from a site or station
 * @param {object} resource - Site or Station object from portals
 * @returns {number|string[]} - [Site Id, Site name]
 */
const getSiteValues = resource => [
  resource.siteId || resource.id,
  resource.site?.name || resource.name,
];

/**
 * Parses API Errors
 * Does some additional parsing on api errors since Portals is inconsistent. Ideally when we
 * standardize the portals errors and use proper status codes etc, this will be the only
 * then-unnecessary function we need to remove
 *
 * @param {object} error - Error from API call
 * @param {string} resource - type of resource to be mentioned in notification
 * @returns {object} - Error object
 */
export const parseApiError = (error, {skipMiddleware = false} = {}) => {
  if (!error) { return null; }
  if (skipMiddleware) return {error: {...error, skipMiddleware: true}};
  if (error.status === FETCH_ERROR_CODE) return {error};

  const errorToReturn = error.status === PARSING_ERROR_CODE
    ? {status: error.originalStatus, message: error.data}
    : error;

  // From portals authService.verifyToken if not logged in
  if (errorToReturn.status !== 401 && errorToReturn.message === 'No authentication provided') {
    errorToReturn.status = 401;
  }

  return errorToReturn;
};

/**
 * Parses API Errors and then formats for middleware
 * @param {object} error - Error from API call
 * @param {string} resource - type of resource to be mentioned in notification
 * @returns {object} - Error object
 */
export const formatErrorForMiddleware = (error, {resource = 'page', skipMiddleware = false} = {}) => {
  const errorToCheck = parseApiError(error);

  const isLoggedIn = checkUserLoggedIn();
  const preface = isLoggedIn ? 'have insufficient privileges' : 'must be logged in';
  const title = isLoggedIn ? 'Missing Permissions' : 'Please Log In';

  if (errorToCheck.status === 401) {
    return {
      error: {
        status: 401,
        message: `You ${preface} to view this ${resource}`,
        title,
      },
    };
  }
  return {error};
};

/**
 * Checks if a user can charge at a site or station
 * @param {object} resource - Site or Station object from portals
 * @param {object} queries - API Query cache data
 * @param {function} sendRequest - API caller
 * @param {object} options - Other options
 * @param {boolean} options.customerLock - True if only same-customer drivers should charge here
 * @returns {object} - Error object if user cannot charge, resource if they can
 */
export const checkSiteAccess = async (resource, queries, sendRequest) => {
  const getCustomerData = async () => {
    const {data: [customer], error: customerError} = await sendRequest('customer');
    if (customerError) throw customerError;
    return [customer.id, customer.name];
  };

  try {
    // no other driver permissions necessary, go ahead and return data
    if (!resource.driverLoginReq) return {data: resource};
    // This should exist here
    const cachedUserData = queries['getUser(undefined)'].data;
    if (cachedUserData?.roleId === ROLES.KITU_ADMIN) return {data: resource};

    // Looks repetitive but we can potentially skip each of these API calls below
    // And also portals should be returning an error statuscode for these cases
    // So this customer-checking stuff is just a failsafe that probably won't get hit
    let userCustomerName;
    let userCustomerId = cachedUserData?.customerId;
    if (!userCustomerId) {
      [userCustomerId, userCustomerName] = await getCustomerData();
    }
    // user belongs to another customer
    if (userCustomerId !== resource.customerId) {
      // may not have this if we got ID from cache
      if (!userCustomerName) {
        [userCustomerId, userCustomerName] = await getCustomerData();
      }
      return {
        error: {
          message: `We're sorry — Your account privileges only allow you to charge at sites belonging to ${userCustomerName}, or any public site.`,
          status: 401,
        },
      };
    }

    // This section is more likely to get hit
    let allowedSites = queries['getAllowedSites(undefined)']?.data;
    if (!allowedSites) {
      const {data, error: getSitesError} = await sendRequest('site');
      if (getSitesError) return formatErrorForMiddleware(getSitesError, {resource: 'site'});
      allowedSites = data.value;
    }
    const [siteId, siteName] = getSiteValues(resource);
    // right customer, but no site access
    if (!allowedSites?.length || !allowedSites.find(allowedSite => allowedSite.id === siteId)) {
      return {
        error: {
          title: 'Missing Permissions',
          message: `You have not been given access to charge at ${siteName}. Please contact a Site Admin to request access.`,
          status: 401,
        },
      };
    }
  } catch (err) {
    return handleError(err);
  }

  return {data: resource};
};
